import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom'; 
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Button from '../Button';

const viewportConfig = { 
threshold: 0.1,
triggerOnce: false,
rootMargin: "-50px 0px"
};

const containerVariants = {
hidden: { opacity: 0, y: 50 },
visible: {
    opacity: 1,
    y: 0,
    transition: {
    duration: 0.5,
    ease: "easeOut",
    staggerChildren: 0.15,
    when: "beforeChildren"
    }
}
};

const itemVariants = {
hidden: { opacity: 0, y: 30 },
visible: {
    opacity: 1,
    y: 0,
    transition: {
    duration: 0.5,
    ease: "easeOut"
    }
}
};

const imageVariants = {
hidden: { opacity: 0, scale: 0.95, x: 50 },
visible: {
    opacity: 1,
    scale: 1,
    x: 0,
    transition: { 
    duration: 0.5, 
    type: "spring", 
    stiffness: 120 
    }
}
};

const ServiceHero = ({ title, description, image, features, isReversed = false }) => {
const [ref, inView] = useInView(viewportConfig);
const controls = useAnimation();

const navigate = useNavigate();
const handleMoreDetails = () => {
    navigate('/product-details', {
      state: { title, description, image, features }
    });
  };
  

React.useEffect(() => {
    controls.start(inView ? "visible" : "hidden");
}, [controls, inView]);

return (
    <Box
    ref={ref}
    component={motion.div}
    variants={containerVariants}
    initial="hidden"
    animate={controls}
    sx={{ 
        width: '100%',
        backgroundColor: '#F7F9FC',
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 }
    }}
    >
    <Box sx={{ 
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
    }}>
        <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md:'row' },
        alignItems: 'center',
        gap: { xs: 4, md: 8 }
        }}>
        <Box 
            component={motion.div}
            variants={imageVariants}
            whileHover={{ 
            scale: 1.03,
            transition: { duration: 0.3 }
            }}
            sx={{ 
            flex: { xs: '1 1 auto', md: '0 0 46%' },
            position: 'relative',
            maxWidth: { xs: '100%', md: '600px' },
            height: { xs: '400px', md: '500px' }
            }}
        >
            <Box
            component="img"
            src={image}
            alt={title}
            sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
            />
        </Box>
        <Box sx={{ flex: 1 }}>
            <Typography 
            component={motion.div}
            variants={itemVariants}
            variant="h4" 
            sx={{ 
                textAlign: 'left', 
                color: '#64B5F6',
                fontWeight: 'bold',
                mb: 4,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                position: 'relative',
                '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                bottom: -10,
                width: '80px',
                height: '4px',
                background: 'linear-gradient(to right, #64B5F6, transparent)',
                borderRadius: '2px'
                }
            }}
            >
            {title}
            </Typography>
            
            <Typography 
            component={motion.div}
            variants={itemVariants}
            variant="h6" 
            sx={{ 
                color: '#666',
                mb: 4,
                lineHeight: 1.6
            }}
            >
            {description}
            </Typography>

            

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {features.map((feature, index) => (
                <Box
                key={index}
                component={motion.div}
                variants={itemVariants}
                custom={index}
                sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start'
                }}
                >
                <Box sx={{ 
                    color: '#64B5F6',
                    mt: 0.5
                }}>
                    <ArrowForwardIcon />
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ 
                    color: '#1A1A1A',
                    mb: 1,
                    fontWeight: 500
                    }}>
                    {feature.title}
                    </Typography>
                    <Typography variant="body1" sx={{ 
                    color: '#666',
                    lineHeight: 1.6
                    }}>
                    {feature.description}
                    </Typography>
                </Box>
                </Box>
            ))}
            </Box>
            <Box 
            component={motion.div}
            variants={itemVariants}
            sx={{ 
                display: 'flex',
                width: '100%',            // make it fill its parent
                justifyContent: {xs:'center',md:'flex-end'}, // push items to the right
                gap: 2,
                mt: 4,
                pr: {xs:0,md:4},
            }}
            >
            <Button 
              variant="dark" 
              href='https://www.mymortgageline.ca/apply-online/'   
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="Apply for a mortgage online"
            >
                Apply Now
            </Button>
            <Button 
              variant="primary" 
              onClick={handleMoreDetails}
              aria-label="View more details about this mortgage service"
            >
                More Details
            </Button>
            </Box>

        </Box>

        
        </Box>
    </Box>
    </Box>
);
};

ServiceHero.propTypes = {
title: PropTypes.string.isRequired,
description: PropTypes.string.isRequired,
image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
features: PropTypes.arrayOf(
    PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired
    })
).isRequired,
isReversed: PropTypes.bool
};

export default ServiceHero; 